import { convertTimeZone } from '@magicdoorinc/date';
import { useLocalization } from '@magicdoorinc/ui';
import type { OpUnitType } from '@magicdoorinc/date';

export const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
export const emailRegex = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
export const backendPhoneRegex = /^\+?[1-9]\d{9,14}$/;

export const normalizePhoneForBackend = (input: string | undefined | null): string | undefined => {
  if (input === undefined || input === null) return undefined;
  const digits = String(input).replace(/\D+/g, '');
  return digits || undefined;
};

export const validatePhone = (input: unknown): input is string => typeof input === 'string' && phoneRegex.test(input);

export const validateEmail = (input: unknown): input is string => typeof input === 'string' && emailRegex.test(input);

export const arrayRequiredValidator = (value: any, name: string, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  const newValue = value ? value : [];

  if (newValue.length === 0 && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  return undefined;
};

export const verifyRequiredValidator = (value: string | undefined | null, name: string, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);

  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  return undefined;
};

export const requiredValidator = (value: string | undefined | null, name: string, length: number, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);

  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  if (value && value.length > length) {
    return t('{name} must be {length} characters or less', { name: translatedName, length: length.toString() });
  }
  return undefined;
};

export const minLengthValidator = (value: string | undefined | null, name: string, minLength: number, isRequired = false) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  if (value && value.length < minLength) {
    return t('{name} must be at least {minLength} characters', { name: translatedName, minLength: minLength.toString() });
  }
  return undefined;
};

const regexValidator = (value: string | undefined | null, pattern: RegExp, errorMessage: string) => {
  const { t } = useLocalization();
  if (value && !pattern.test(value)) {
    return t(errorMessage);
  }
  return undefined;
};

const rangeValidator = (value: string | undefined | null, name: string, min: number, max: number, isRequired = false) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  if (value && (value.length < min || value.length > max)) {
    return t('{name} must be between {min} and {max} characters', { name: translatedName, min: min.toString(), max: max.toString() });
  }
  return undefined;
};

export const equalLengthValidator = (value: string | undefined, name: string, length: number, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  if (value && value.length !== length) {
    return t('{name} must be {length} characters', { name: translatedName, length: length.toString() });
  }
  return undefined;
};

export const dateFormatValidator = (value: string | undefined | null, name: string, formatString: string, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  if (!value && isRequired) {
    return t('{name} is required', { name: translatedName });
  }
  if (!convertTimeZone(value).isValid()) {
    return t('{name} date string is incorrect', { name: translatedName });
  }
  return undefined;
};

export const minNumberValidator = (value: number | string | undefined, name: string, min: number, isRequired: boolean) => {
  const { t } = useLocalization();
  const translatedName = t(name);
  if (isRequired && (value === undefined || value === null || value === '')) {
    return t('{name} is required', { name: translatedName });
  }
  if (typeof value === 'string' || isNaN(value as number)) {
    const num = parseFloat(value as string);
    if (isNaN(num) || !isFinite(num)) {
      return t('{name} is not a number', { name: translatedName });
    }
    value = num;
  }
  if (typeof value !== 'number') {
    return t('{name} is not a number', { name: translatedName });
  }
  if (value < min) {
    return t('{name} must be at least greater than {min}', { name: translatedName, min: min.toString() });
  }
  return undefined;
};

export const getStreetAddress1ValidationError = (streetAddress1: string | undefined | null, isRequired: boolean) => {
  return requiredValidator(streetAddress1, 'Street address 1', 150, isRequired);
};

export const getStreetAddress2ValidationError = (streetAddress2: string | undefined | null, isRequired: boolean) => {
  return (
    requiredValidator(streetAddress2, 'Street address 2', 150, isRequired) || minLengthValidator(streetAddress2, 'Street address 2', 2)
  );
};

export const getCityValidationError = (city: string | undefined | null, isRequired: boolean) => {
  return requiredValidator(city, 'City', 150, isRequired) || minLengthValidator(city, 'City', 2);
};

export const getStateValidationError = (state: string | undefined | null, isRequired: boolean) => {
  return requiredValidator(state, 'State', 2, isRequired) || regexValidator(state, /^[A-Za-z]{2}$/, 'State must be 2 characters');
};

export const getZipCodeValidationError = (zipCode: string | undefined | null, isRequired: boolean) => {
  return requiredValidator(zipCode, 'Zip code', 5, isRequired) || regexValidator(zipCode, /^\d{5}$/, 'Zip code must be 5 digits');
};

export const getCountryValidationError = (country: string | undefined | null, isRequired: boolean) => {
  return rangeValidator(country, 'Country', 3, 25, isRequired);
};

export const getUrlValidator = (url: string | undefined | null) => {
  if (url) {
    return /^(https?:\/\/(([a-zA-Z0-9]+-?)+[a-zA-Z0-9]+\.)+[a-zA-Z]+)(:\d+)?(\/.*)?(\?.*)?(#.*)?$/.test(url);
  } else {
    return true;
  }
};

export const getSubUrlValidator = (url: string | undefined | null) => {
  if (url) {
    return /^[a-z0-9][a-z0-9-_]+$/.test(url);
  } else {
    return true;
  }
};

export const getDomainValidator = (domain: string | undefined | null) => {
  if (domain) {
    return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(domain);
  } else {
    return true;
  }
};

export const validateDateComparison = (
  inputDate: string | Date | undefined | null,
  name: string,
  referenceDate: string | Date,
  comparison: 'isAfter' | 'isBefore' | 'isSame' | 'isSameOrAfter' | 'isSameOrBefore',
  unit: OpUnitType = 'day',
  isRequired: boolean = false
): string | undefined => {
  const { t } = useLocalization();
  const translatedName = t(name);

  if (!inputDate && isRequired) {
    return t('{name} is required', { name: translatedName });
  }

  if (!inputDate) {
    return undefined;
  }

  const inputDateObj = convertTimeZone(inputDate);
  const referenceDateObj = convertTimeZone(referenceDate);

  if (!inputDateObj.isValid()) {
    return t('{name} is not a valid date', { name: translatedName });
  }

  if (!referenceDateObj.isValid()) {
    return t('Reference date is not valid');
  }

  let isValid: boolean;
  if (comparison === 'isSame' && unit) {
    isValid = inputDateObj.isSame(referenceDateObj, unit);
  } else {
    isValid = inputDateObj[comparison](referenceDateObj);
  }

  if (!isValid) {
    const comparisonText = {
      isAfter: 'after',
      isBefore: 'before',
      isSame: 'the same as',
      isSameOrAfter: 'the same as or after',
      isSameOrBefore: 'the same as or before',
    }[comparison];

    if (comparison === 'isSame' && unit) {
      return t('{name} must be the same {unit} as {referenceDate}', {
        name: translatedName,
        unit: t(unit),
        referenceDate: referenceDateObj.format('YYYY-MM-DD'),
      });
    }

    return t('{name} must be {comparison} {referenceDate}', {
      name: translatedName,
      comparison: comparisonText,
      referenceDate: referenceDateObj.format('YYYY-MM-DD'),
    });
  }

  return undefined;
};

/**
 * Checks if email content is essentially empty (only contains HTML tags, whitespace, and non-breaking spaces)
 * @param content - The email content to validate
 * @returns true if content is essentially empty, false otherwise
 */
export const isEmailContentEmpty = (content: string | undefined | null): boolean => {
  if (!content) return true;

  // Remove HTML tags and decode HTML entities
  const textContent = content
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces with regular spaces
    .replace(/&amp;/g, '&') // Replace common HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim(); // Remove leading/trailing whitespace

  return textContent.length === 0;
};

/**
 * Validates email content to ensure it's not empty
 * @param content - The email content to validate
 * @param name - The field name for error messages
 * @param isRequired - Whether the field is required
 * @returns Error message if validation fails, undefined otherwise
 */
export const validateEmailContent = (content: string | undefined | null, name: string, isRequired: boolean): string | undefined => {
  const { t } = useLocalization();
  const translatedName = t(name);

  if (!content && isRequired) {
    return t('{name} is required', { name: translatedName });
  }

  if (content && isEmailContentEmpty(content)) {
    return t('{name} cannot be empty', { name: translatedName });
  }

  return undefined;
};
